/**
 * Money formatting helpers.
 *
 * Design rule: the currency code is ALWAYS displayed alongside any monetary
 * value. We never silently assume a currency, never silently convert between
 * currencies. If the upstream source preserved CAD, the UI shows "CAD".
 */
import i18n from "../i18n";

/**
 * Default currency used only when the upstream data carries none (e.g.
 * portfolio-wide aggregates with no single native currency). Canada is
 * Qenva's primary market. Overridden per-workspace once the currency
 * selector (step 2) lands.
 */
export const DEFAULT_CURRENCY = "CAD";

const LOCALE_TAG = { fr: "fr-CA", en: "en-CA" };
const FALLBACK_LOCALE_TAG = "fr-CA";

/**
 * Resolve the Intl locale tag from the active i18next language, so every
 * money string (grouping, decimal separator) follows the app's language
 * toggle — never the browser/OS locale.
 */
function activeLocaleTag() {
  const lng = (i18n?.language || "").toString().toLowerCase().split(/[-_]/)[0];
  return LOCALE_TAG[lng] || FALLBACK_LOCALE_TAG;
}

/**
 * Format a monetary value with its currency code.
 * @param {number|string|null|undefined} amount  raw magnitude (no scaling)
 * @param {string|null|undefined} currency       ISO-4217 code (USD, CAD, EUR, …)
 * @param {object} opts                          { compact: bool, minimumFractionDigits: number }
 * @returns {string|null}                        formatted string, or null when
 *                                                amount is missing
 */
export function fmtMoney(amount, currency, opts = {}) {
  if (amount == null || isNaN(Number(amount))) return null;
  const v = Number(amount);
  const code = (currency || DEFAULT_CURRENCY).toString().toUpperCase();
  const symbol = SYMBOLS[code] || "";
  // Single-glyph symbols ($ € £ ¥ ₣ ₹ …) sit flush against the number per
  // standard typographic convention. Multi-character abbreviations used as
  // a symbol (DA, DH, R$) need a separating space or they read as a glued,
  // truncated code (e.g. "DA0") rather than a symbol + amount.
  const symbolSep = symbol.length > 1 ? " " : "";
  const compact = opts.compact !== false; // default ON
  const minFrac = opts.minimumFractionDigits;
  const tag = activeLocaleTag();

  let body;
  if (compact && Math.abs(v) >= 1_000_000) {
    const n = (v / 1_000_000).toLocaleString(tag, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    body = `${symbol}${symbolSep}${n}M`;
  } else if (compact && Math.abs(v) >= 1_000) {
    const n = (v / 1_000).toLocaleString(tag, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    body = `${symbol}${symbolSep}${n}K`;
  } else {
    const n = v.toLocaleString(tag, {
      minimumFractionDigits: minFrac,
      maximumFractionDigits: Math.max(minFrac ?? 0, 2),
    });
    body = `${symbol}${symbolSep}${n}`;
  }
  // Always append the currency code so USD/CAD are never ambiguous.
  return `${body} ${code}`;
}

/**
 * Format just the currency code as a small label.
 */
export function currencyLabel(currency) {
  return (currency || DEFAULT_CURRENCY).toString().toUpperCase();
}

/**
 * Symbols used as a *visual hint*. Currency code is always shown alongside.
 * "$" alone is ambiguous (USD vs CAD vs AUD), hence the suffix.
 */
const SYMBOLS = {
  USD: "$", CAD: "$", AUD: "$", NZD: "$", SGD: "$", HKD: "$", MXN: "$",
  EUR: "€", GBP: "£", JPY: "¥", CNY: "¥", CHF: "₣",
  INR: "₹", BRL: "R$", ZAR: "R", MAD: "DH", DZD: "DA",
};

/**
 * List used by manual-entry currency selectors and the workspace currency
 * selector (Settings). Order is by frequency for Covara's primary user base
 * (NA + EU SMBs), with DZD appended last. Mirrors backend/currency_defaults.py's
 * SUPPORTED_CURRENCIES — keep both in sync when adding a currency.
 */
export const SUPPORTED_CURRENCIES = [
  "USD", "CAD", "EUR", "GBP", "AUD", "JPY", "CHF",
  "MXN", "BRL", "INR", "CNY", "HKD", "SGD", "NZD", "ZAR", "MAD", "DZD",
];
